/**
 * Blob URL Management Utilities
 * 
 * Manages lifecycle of blob URLs created with URL.createObjectURL()
 * to prevent memory leaks in the browser.
 * 
 * Blob URLs are temporary URLs that point to data in browser memory.
 * They MUST be revoked with URL.revokeObjectURL() when no longer needed,
 * otherwise they persist and waste memory.
 */

/**
 * Track all created blob URLs for debugging
 */
const activeBlobUrls = new Set<string>();

/**
 * Create a blob URL and track it
 * 
 * @param blob - The Blob or File to create URL for
 * @returns The created blob URL
 */
export const createBlobURL = (blob: Blob | File): string => {
  const url = URL.createObjectURL(blob);
  activeBlobUrls.add(url);
  console.log(`📦 Created blob URL: ${url.substring(0, 50)}... (Total active: ${activeBlobUrls.size})`);
  return url;
};

/**
 * Revoke a blob URL and remove from tracking
 * 
 * @param url - The blob URL to revoke
 */
export const revokeBlobURL = (url: string): void => {
  if (!url || !url.startsWith('blob:')) {
    console.warn(`⚠️ Attempted to revoke non-blob URL: ${url}`);
    return;
  }

  try {
    URL.revokeObjectURL(url);
    activeBlobUrls.delete(url);
    console.log(`✅ Revoked blob URL: ${url.substring(0, 50)}... (Total active: ${activeBlobUrls.size})`);
  } catch (error) {
    console.error(`❌ Failed to revoke blob URL: ${error}`);
  }
};

/**
 * Revoke multiple blob URLs
 * 
 * @param urls - Array of blob URLs to revoke
 */
export const revokeMultipleBlobURLs = (urls: string[]): void => {
  console.log(`🧹 Revoking ${urls.length} blob URLs...`);
  
  const blobUrls = urls.filter(url => url && url.startsWith('blob:'));
  
  blobUrls.forEach(url => revokeBlobURL(url));
  
  console.log(`✅ Revoked ${blobUrls.length} blob URLs`);
};

/**
 * Extract and revoke blob URLs from media items
 * 
 * @param mediaItems - Media items that may contain blob URLs
 */
export const revokeBlobURLsFromMediaItems = (mediaItems: any[]): void => {
  const blobUrls: string[] = [];

  mediaItems.forEach(item => {
    if (item.url && item.url.startsWith('blob:')) {
      blobUrls.push(item.url);
    }
    if (item.src && item.src.startsWith('blob:')) {
      blobUrls.push(item.src);
    }
  });

  if (blobUrls.length > 0) {
    console.log(`🧹 Found ${blobUrls.length} blob URLs in media items`);
    revokeMultipleBlobURLs(blobUrls);
  }
};

/**
 * Replace blob URLs with permanent URLs and revoke old blobs
 * 
 * @param mediaItems - Media items with blob URLs
 * @param permanentUrls - Mapping of old blob URLs to new permanent URLs
 * @returns Updated media items with permanent URLs
 */
export const replaceBlobURLsWithPermanent = <T extends { url?: string; src?: string; id: string }>(
  mediaItems: T[],
  urlMapping: Map<string, string>
): T[] => {
  const blobUrlsToRevoke: string[] = [];

  const updatedItems = mediaItems.map(item => {
    const updated = { ...item };
    
    // Replace url field
    if (item.url && item.url.startsWith('blob:')) {
      const permanentUrl = urlMapping.get(item.id);
      if (permanentUrl) {
        blobUrlsToRevoke.push(item.url);
        updated.url = permanentUrl;
      }
    }
    
    // Replace src field
    if (item.src && item.src.startsWith('blob:')) {
      const permanentUrl = urlMapping.get(item.id);
      if (permanentUrl) {
        blobUrlsToRevoke.push(item.src);
        updated.src = permanentUrl;
      }
    }
    
    return updated;
  });

  // Revoke all old blob URLs
  if (blobUrlsToRevoke.length > 0) {
    console.log(`🔄 Replacing ${blobUrlsToRevoke.length} blob URLs with permanent URLs`);
    revokeMultipleBlobURLs(blobUrlsToRevoke);
  }

  return updatedItems;
};

/**
 * Get count of active blob URLs (for debugging)
 * 
 * @returns Number of tracked blob URLs
 */
export const getActiveBlobCount = (): number => {
  return activeBlobUrls.size;
};

/**
 * Get all active blob URLs (for debugging)
 * 
 * @returns Array of active blob URLs
 */
export const getActiveBlobURLs = (): string[] => {
  return Array.from(activeBlobUrls);
};

/**
 * Revoke all tracked blob URLs (emergency cleanup)
 * Use with caution - only call when absolutely necessary
 */
export const revokeAllBlobURLs = (): void => {
  console.warn(`🚨 Emergency: Revoking ALL ${activeBlobUrls.size} blob URLs`);
  
  activeBlobUrls.forEach(url => {
    try {
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(`Failed to revoke: ${url}`, error);
    }
  });
  
  activeBlobUrls.clear();
  console.log('✅ All blob URLs revoked');
};

/**
 * Check if a URL is a blob URL
 * 
 * @param url - URL to check
 * @returns true if URL is a blob URL
 */
export const isBlobURL = (url: string | undefined | null): boolean => {
  return Boolean(url && url.startsWith('blob:'));
};

/**
 * Start monitoring blob URL usage (for debugging)
 * Logs warning if blob URL count exceeds threshold
 * 
 * @param threshold - Warning threshold (default: 20)
 * @param intervalMs - Check interval (default: 5000ms)
 * @returns Function to stop monitoring
 */
export const startBlobURLMonitoring = (
  threshold: number = 20,
  intervalMs: number = 5000
): (() => void) => {
  console.log(`📊 Starting blob URL monitoring (threshold: ${threshold}, interval: ${intervalMs}ms)`);
  
  const interval = setInterval(() => {
    const count = activeBlobUrls.size;
    
    if (count > 0) {
      console.log(`📦 Active blob URLs: ${count}`);
    }
    
    if (count > threshold) {
      console.warn(`⚠️ High blob URL count: ${count} (threshold: ${threshold})`);
      console.warn('Active blob URLs:', getActiveBlobURLs());
    }
  }, intervalMs);

  return () => {
    clearInterval(interval);
    console.log('🛑 Blob URL monitoring stopped');
  };
};

// Export for convenience
export const blobURLUtils = {
  create: createBlobURL,
  revoke: revokeBlobURL,
  revokeMultiple: revokeMultipleBlobURLs,
  revokeFromMediaItems: revokeBlobURLsFromMediaItems,
  replaceWithPermanent: replaceBlobURLsWithPermanent,
  getActiveCount: getActiveBlobCount,
  getActiveURLs: getActiveBlobURLs,
  revokeAll: revokeAllBlobURLs,
  isBlob: isBlobURL,
  startMonitoring: startBlobURLMonitoring,
};
